import React, {Fragment} from 'react';
import Paper from '@mui/material/Paper';
import classNames from 'classnames';
import UndoIcon from '@mui/icons-material/Undo';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import StatusMenu from '../StatusMenu';
import Tooltip from '@mui/material/Tooltip';
import { handleJurorCardClick } from '../../../state/asyncActions';
import { restoreJurorToBox, updateJuror, swapJurorToOther, buildRestoreToPoolAlert } from '../../../db/actions';
import {useDispatch, useSelector} from 'react-redux';
import {getJurorDemographics, getJurorError} from '../../../state/selectors';
import Alert from '@mui/material/Alert';
import {ActionCreators} from '../../../state/actions';
import MultiSelectModes from "../../../state/multiSelect/modes";
import {Button} from '@mui/material';
import Display from '../../../components/RichTextEditor/Display';
import {CheckBoxOutlineBlank, CheckBoxOutlined} from '@mui/icons-material';
import Typography from '@mui/material/Typography';
import {ActionCreators as EtherealActionCreators} from '../../../state/ethereal/actions';
import EmojiIndex from "../EmojiIndex";
import JurorEditingBadge from '../../../collaboration/JurorEditingBadge';

import useStyles from './JurorCard.styles.js';
import {getActiveDrag} from "../../../state/ethereal/selectors";
import {useViewBasedCardWidth} from "../../../util/hooks";
import SeatNumberEditor from '../SeatNumberEditor';

const JurorError = ({caseId, juror, jurorError}) => {
    const dispatch = useDispatch();
    const classes = useStyles();

    function handleClose(e) {
        e.preventDefault();
        e.stopPropagation();
        dispatch(ActionCreators.removeJurorError(juror._id));
    }

    function handleRetry(e) {
        e.preventDefault();
        e.stopPropagation();
        dispatch(updateJuror(caseId, juror._id, juror));
    }

    if (!jurorError) {
        return null;
    }

    return (
        <Alert key={`juror-error-${juror._id}`}
               className={classes.error}
               severity={'error'}
               onClose={handleClose}>
            {jurorError.error ? jurorError.error.toString() : 'An error occurred, please try again'}
            <Button color="inherit" size="small" onClick={handleRetry}>Retry</Button>
        </Alert>
    );
};

const JurorCard = ({
                       caseId,
                       demographicCategories,
                       showDemographics,
                       jurorId,
                       juror,
                       mode,
                       seatNumber,
                       spaceNumber,
                       defaultSeatNumber,
                       isCustomSeatNumber = false,
                       isConflictSeatNumber = false,
                       type,
                       multiSelect,
                       searchActive = false,
                       isSearchMatch = false
                   }) => {
    const classes = useStyles();
    const dispatch = useDispatch();

    // Subscribe to a boolean instead of the raw drag id so that starting or
    // ending a drag only re-renders the dragged card, not every card.
    const isDragSource = useSelector(
        (state) => juror != null && getActiveDrag(state) === juror._id,
    );

    const minimumJurorCardWidth = useViewBasedCardWidth();

    const jurorError = useSelector((state) => getJurorError(state, {jurorId: juror ? juror._id : 0}));

    const handleClick = async () => {
        //console.log("Handling click...");
        dispatch(handleJurorCardClick(caseId, juror, seatNumber));
    };

    const handleEmojiClick = (emoji, occurrence) => {
        dispatch(ActionCreators.openJurorModal(caseId, juror, seatNumber, mode, emoji, occurrence));
    }

    const onStart = (e) => {
        dispatch(EtherealActionCreators.setDrag(jurorId));
        e.dataTransfer.setData('text/plain', jurorId);
    }

    const dragStop = (e) => {
        dispatch(EtherealActionCreators.setDrag(null));
    };

    const handleDrag = (e) => {
        const width = window.document.documentElement.clientWidth;
        const height = window.document.documentElement.clientHeight;

        const jbElem = document.getElementById("jury-box-grid-container");

        const pageElem = document.documentElement;

        if (e.pageX < 100) {
            jbElem.scroll({left: jbElem.scrollLeft - 2, behavior: "instant"});
        }

        if (e.pageX > width - 100) {
            jbElem.scroll({left: jbElem.scrollLeft + 2, behavior: "instant"});
        }

        if (e.pageY - pageElem.scrollTop < 100) {
            pageElem.scroll({top: pageElem.scrollTop - 2, behavior: "instant"});
        }

        if (e.pageY - pageElem.scrollTop > height - 100) {
            pageElem.scroll({top: pageElem.scrollTop + 2, behavior: "instant"});
        }

    };

    const restoreJuror = async (e) => {
        e.stopPropagation();
        const { placedInPool } = await restoreJurorToBox(caseId, juror._id, mode);
        if (placedInPool) {
            const alert = buildRestoreToPoolAlert(mode);
            dispatch(EtherealActionCreators.addAlert(alert.message, alert.title, alert.variant));
        }
    };

    const updateStatus = async (status) => {
        const newJurorInfo = {...juror, status: status};
        await updateJuror(caseId, juror._id, newJurorInfo, { undoLabel: 'Change juror status' });
    };

    const swapToOther = () => {
        swapJurorToOther(caseId, juror._id, mode);
    }

    const getSymbol = () => {
        let symbol = '';
        if (juror.discarder === 'defense') {
            symbol = 'D';
        } else if (juror.discarder === 'prosecutor') {
            symbol = 'P';
        } else if (juror.discarder === "both") {
            symbol = 'B';
        }
        if (juror.discardedReason === 'cause') {
            symbol = 'C';
        }
        if (juror.discardedReason === 'hardship') {
            symbol = 'H';
        }
        return symbol;
    };

    let className = '';
    let symbol = '';
    if (type === 'juror') {
        className = classNames(classes.header, classes.juror);
    } else {
        className = classNames(classes.header, classes.dismissed);
        symbol = getSymbol();
    }

    if (juror) {
        let demographicsItems = '';
        if (juror.demographics) {

            demographicsItems = getJurorDemographics(juror, demographicCategories);

            demographicsItems = demographicsItems.map((demographic, index) => {
                return (
                    <Fragment key={demographic.name + '-' + index}>
                        <Tooltip key={demographic.name} title={demographic.name}>
                            <span>
                              {demographic.value}
                            </span>
                        </Tooltip>
                        {index < demographicsItems.length - 1 && ", "}
                    </Fragment>
                );
            });
        }
        let jurorID = '';
        if (juror.hasOwnProperty('nameAndId')) {
            jurorID = juror.nameAndId;
        } else if (juror.id) {
            jurorID = juror.id;
        }

        if (juror.discarded || juror.alternateDiscarded) {
            return (
                // Dismissed juror card
                <div style={{position: "relative"}} className="discarded-juror">
                    <Box sx={{position: 'absolute', top: 26, right: 14, zIndex: 5}}>
                        <JurorEditingBadge jurorId={juror._id} />
                    </Box>
                    <Paper
                        elevation={3}
                        className={classNames(classes.paper, classes.handle)}
                        onClick={handleClick}
                    >
                        <Box display="flex" p={1} className={className}>
                            <Box flexGrow={1} className={classNames(classes.header, classes.idBox)}>
                                <b>{jurorID}</b>
                            </Box>
                            <Box className={classes.header}>
                                {symbol}
                            </Box>
                            <Box className={classes.header}>
                                <Tooltip title="Restore Juror">
                                    <IconButton
                                        style={{padding: 0, color: 'black'}}
                                        onClick={(e) => restoreJuror(e)}
                                        size="large">
                                        <UndoIcon fontSize='small'/>
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>
                        {showDemographics &&
                            <Box className={classes.demographics}>
                                <Typography variant={"body1"}
                                            className={classes.demographicsText}>{demographicsItems}</Typography>
                            </Box>
                        }
                        <Box className={classes.content}>
                            <Display richTextStyles={juror.commentsStyles || []} text={juror.comments || ""}/>
                        </Box>
                        <JurorError caseId={caseId} juror={juror} jurorError={jurorError}/>
                    </Paper>
                    <EmojiIndex text={juror.comments || ""}
                                onClick={handleEmojiClick}
                                className={classes.emojiIndex}/>
                </div>
            );
        }

        let rightComponent = (
                <StatusMenu
                    updateStatus={updateStatus}
                    mode={mode}
                    onSwap={swapToOther}
                    caseId={caseId}
                    jurorId={jurorId}
                    size={"sm"}
                    status={juror.status ? juror.status : null}/>
            ),
            extraProps = {},
            cardStyles = {
                flex: 1,
                minWidth: minimumJurorCardWidth,
                backgroundColor: "transparent",
                position: "relative",
                transition: "opacity 200ms ease, box-shadow 200ms ease",
            };

        // Jury box search: dim non-matches (never hide); matches get an
        // amber outline on the Paper itself (see paperClasses below) so the
        // ring hugs the card's visual edge rather than the flex cell.
        if (searchActive && !isSearchMatch) {
            cardStyles.opacity = 0.35;
        }
        if (multiSelect && multiSelect.mode === MultiSelectModes.ON) {
            extraProps.onClick = handleClick;
            cardStyles.cursor = "pointer";
            if (multiSelect.selectedJurorIds.indexOf(juror._id) >= 0) {
                rightComponent = (
                    <CheckBoxOutlined style={{fontSize: 20, marginBottom: "-4px"}}/>
                );
            } else {
                rightComponent = (
                    <CheckBoxOutlineBlank style={{fontSize: 20, marginBottom: "-4px"}}/>
                );
            }
        }

        let paperClasses = [classes.paper];
        if (isDragSource) {
            paperClasses.push(classes.outline);
        }
        if (searchActive && isSearchMatch) {
            paperClasses.push(classes.searchMatch);
        }

        let contentStyles = {flex: 1};
        if (isDragSource) {
            contentStyles.display = "none";
        }

        return (
            // Populated juror card
            <div
                style={cardStyles}
                className="juror-card"
                draggable={!(multiSelect && multiSelect.mode === MultiSelectModes.ON)}
                onDragStart={onStart}
                onDragEnd={dragStop}
                onDrag={handleDrag}
                onClick={handleClick}
                id={juror._id}
                {...extraProps}
            >
                <Box sx={{position: 'absolute', top: 26, right: 14, zIndex: 5}}>
                    <JurorEditingBadge jurorId={juror._id} />
                </Box>
                <Paper
                    elevation={3}
                    className={classNames(...paperClasses)}
                >
                    <Box display="flex" p={1} className={className}>
                        <Box flexGrow={1} className={classNames(classes.header, classes.idBox)}>
                            <div className={classNames(classes.handle, 'handle')}>
                                <Box className={classes.idBox}>
                                    <b>{jurorID}</b>
                                </Box>
                            </div>
                        </Box>
                        <Box className={classes.header}>
                            <div className={classNames(classes.handle, 'handle')}>
                                {spaceNumber != null ? (
                                    <SeatNumberEditor
                                        caseId={caseId}
                                        mode={mode}
                                        spaceNumber={spaceNumber}
                                        seatNumber={seatNumber}
                                        defaultSeatNumber={defaultSeatNumber ?? seatNumber}
                                        isCustom={isCustomSeatNumber}
                                        isConflict={isConflictSeatNumber}
                                    />
                                ) : (
                                    <b>{seatNumber}</b>
                                )}
                            </div>
                        </Box>
                        <Box className={classes.header}>
                            {rightComponent}
                        </Box>
                    </Box>
                    <div className={classNames(classes.handle, 'handle')} style={contentStyles}>
                        {showDemographics &&
                            <Box className={classes.demographics}>
                                <Typography variant={"body1"}
                                            className={classes.demographicsText}>{demographicsItems}</Typography>
                            </Box>
                        }
                        <Box className={classes.content}>
                            <Display richTextStyles={juror.commentsStyles || []} text={juror.comments || ""}/>
                        </Box>
                    </div>
                    <JurorError caseId={caseId} juror={juror} jurorError={jurorError}/>
                </Paper>
                <EmojiIndex text={juror.comments || ""}
                            onClick={handleEmojiClick}
                            className={classes.emojiIndex}/>
            </div>
        );
    }
};

// Memoized so a juror write only re-renders the affected cards. This relies
// on useCasePageReady keeping object references stable for unchanged jurors
// and on JuryBox passing referentially-stable callbacks.
export default React.memo(JurorCard);
